import { Component, OnChanges, Output, Input, EventEmitter, SimpleChange } from "@angular/core";

const THRESHOLD_FOR_SHOWING_CONVENIENT_PAGE_LINKS = 10;

interface IPageLink {
  text?: string;
  pageNumber?: number;
  disabled?: boolean;
  next?: boolean;
  previous?: boolean;
  active?: boolean;
}

export interface PaginationOptions {
  showPageLinks?: boolean;
  showPagingSummary?: boolean;
  size?: number;
  shouldShowFirstPageLink?: boolean;
  shouldShowNextPageLink?: boolean;
  shouldShowPreviousPageLink?: boolean;
  shouldShowLastPageLink?: boolean;
  small?: boolean;
  large?: boolean;
}

export interface PaginationModel {
  pageStartingIndex: number;
  pageEndingIndex: number;
  totalCount: number;
  pageIndex: number;
  totalPages: number;
  pageSize: number;
}

@Component({
  selector: "pager",
  templateUrl: "./pager.html",
  standalone: false,
})
export class Pager implements OnChanges {
  @Input() options: PaginationOptions;
  @Input() items: PaginationModel;
  @Output() pageChanged: EventEmitter<number> = new EventEmitter<number>();
  pages: IPageLink[] = [];

  ngOnChanges(changes: { [propertyName: string]: SimpleChange }) {
    for (let propName in changes) {
      if (propName == "items") {
        this.pages = this.computePages();
        break;
      }
    }
  }

  computePages(): IPageLink[] {
    if (!this.options) this.options = {};

    let pages: IPageLink[] = [];

    if (!this.items || this.items.totalPages < 2) return pages;

    let totalPages = this.items.totalPages;
    let pageIndex = this.items.pageIndex;

    if (this.shouldShowFirstPageLink()) {
      pages.push({
        text: "«« ",
        previous: true,
        pageNumber: 1,
      });
    }

    if (this.shouldShowPreviousPageLink()) {
      pages.push({ text: "«  ", previous: true, pageNumber: pageIndex - 1 });
    }

    if (totalPages < THRESHOLD_FOR_SHOWING_CONVENIENT_PAGE_LINKS) {
      this.addPages(pages, 1, totalPages);
    } else {
      let length = this.options.size || 3;
      this.addPages(pages, 1, length);
      if (pageIndex >= (length + 1) * 2) {
        this.addEllipsis(pages);
      }
      this.addPages(
        pages,
        Math.max(pageIndex - length, length + 1),
        Math.min(pageIndex + length, totalPages)
      );
      if (pageIndex < totalPages - (length * 2 + 1)) {
        this.addEllipsis(pages);
      }
      this.addPages(pages, Math.max(totalPages - length, pageIndex + length + 1), totalPages);
    }

    if (this.shouldShowNextPageLink()) {
      pages.push({ text: "  »", next: true, pageNumber: pageIndex + 1 });
    }

    if (this.shouldShowLastPageLink()) {
      pages.push({ text: " »»", next: true, pageNumber: totalPages });
    }

    return pages;
  }

  addPages(pages: IPageLink[], from: number, to: number): void {
    for (let pageIndex = from; pageIndex <= to; pageIndex++) {
      let pageLink: IPageLink = {};

      if (pageIndex == this.items.pageIndex) {
        pageLink.active = true;
      } else {
        pageLink.pageNumber = pageIndex;
      }
      pageLink.text = pageIndex.toString();
      pages.push(pageLink);
    }
  }

  addEllipsis(pages: IPageLink[]) {
    pages.push({ text: "...", disabled: true });
  }

  haveMorePages(): boolean {
    return this.items.pageIndex * this.items.pageSize < this.items.totalCount;
  }

  havePreviousPages(): boolean {
    return this.items.pageIndex > 1;
  }

  shouldShowFirstPageLink(): boolean {
    return (
      this.items.totalPages > THRESHOLD_FOR_SHOWING_CONVENIENT_PAGE_LINKS &&
      this.items.pageIndex - 1 > 1 &&
      this.options.shouldShowFirstPageLink !== false
    );
  }

  shouldShowPreviousPageLink(): boolean {
    return (
      this.items.totalPages > THRESHOLD_FOR_SHOWING_CONVENIENT_PAGE_LINKS &&
      this.havePreviousPages() &&
      this.options.shouldShowPreviousPageLink !== false
    );
  }

  shouldShowNextPageLink(): boolean {
    return (
      this.items.totalPages > THRESHOLD_FOR_SHOWING_CONVENIENT_PAGE_LINKS &&
      this.haveMorePages() &&
      this.options.shouldShowNextPageLink !== false
    );
  }

  shouldShowLastPageLink(): boolean {
    return (
      this.items.totalPages > THRESHOLD_FOR_SHOWING_CONVENIENT_PAGE_LINKS &&
      (this.items.pageIndex + 1) * this.items.pageSize < this.items.totalCount &&
      this.options.shouldShowLastPageLink !== false
    );
  }

  selectPage(pageNumber: number, event?: MouseEvent) {
    if (event) event.preventDefault();

    this.pageChanged.emit(pageNumber);
  }
}
